import { injectable } from 'inversify';
import { AnvilSelectOptionsProps } from '@servicetitan/design-system';
import { TableState, InMemoryDataSource as InMemoryGridDataSource } from '@servicetitan/table';
import { DropdownState, IDropdownState, InMemoryDataSource } from '@servicetitan/form';
import { FieldState } from 'formstate';
import { computed, observable, runInAction, action } from 'mobx';
import { StatementsApiService } from '../../api/api/statementsApi.service';
import { CustomerModel } from 'src/api';
import { address } from 'src/util/formatters';
import { ChargeStore } from './charge.store';
import moment from 'moment';
import { Invoice } from 'src/api/model/invoice';
import { RouterStore } from 'src/core/stores/router.store';
import { ClientDataStore } from 'src/core/stores/client-data.store';

export interface InvoiceRowModel {
    invoiceId: string;
    invoiceNumber?: string;
    serviceDate?: Date;
    serviceAddress?: string;
    dueDate?: Date;
    invoiceDate?: Date;
    invoiceTotal: number;
    paymentTotal: number;
    invoiceBalance: number;
    usesPaymentsService: boolean;
}

export enum InvoiceFilter {
    All = 1,
    PastDue = 2,
    Statement = 3,
}

export interface ExtendedCustomerModel extends CustomerModel {
    balance: number;
}

export interface CustomerAnvilSelectOptionsProps extends AnvilSelectOptionsProps {
    text: string;
    value: string;
}

const DEFAULT_FILTER = InvoiceFilter.Statement;

@injectable()
export class StatementStore {
    public gridState = new TableState<InvoiceRowModel, string>({
        pageSize: 20,
    });
    @observable selectedCustomer?: CustomerModel;
    @observable selectedFilter: InvoiceFilter = DEFAULT_FILTER;
    @observable customerDropdownState!: IDropdownState<string, CustomerAnvilSelectOptionsProps>;
    @observable customerMap: Map<string, ExtendedCustomerModel> = new Map<
        string,
        ExtendedCustomerModel
    >();
    searchTerm: FieldState<string> = new FieldState('').onUpdate(
        async (state) => await this.customerDropdownState.onSearchChange(state.value)
    );

    constructor(
        private readonly statementApiService: StatementsApiService,
        private readonly chargeInfoService: ChargeStore,
        private readonly router: RouterStore,
        private readonly clientData: ClientDataStore
    ) { }

    initialize = async (statementId?: string) => {
        try {
            if (!this.selectedCustomer || this.chargeInfoService.paymentCompleted) {
                const response = statementId
                    ? await this.statementApiService.getCustomerStatementsById(statementId, true)
                    : await this.statementApiService.getCustomerStatements(true);

                const preSelectedCustomerId =
                    statementId || response.data.customers!.length === 1
                        ? response.data.customers![0].id
                        : undefined;

                await runInAction(async () => {
                    this.chargeInfoService.paymentCompleted = false;
                    this.chargeInfoService.initializeFromStatementModel(response.data!);
                    this.initCustomerMap(response.data.customers);
                    this.initCustomerDropdownState(response.data.customers);
                    this.selectCustomer(preSelectedCustomerId);
                });
            }
        } catch (e) {
            await runInAction(() => {
                this.router.history.replace('/statement/not-found');
            });
        }
    };

    @action selectCustomer = async (customerId?: string) => {
        !customerId
            ? (this.selectedCustomer = undefined)
            : (this.selectedCustomer = this.customerMap.get(customerId!));
        this.initInvoiceFilter();
        this.clearSearch();
        await this.setDataSource();
    };

    initInvoiceFilter = () => {
        this.selectedFilter = this.clientData.isLoggedIn
            ? InvoiceFilter.All
            : this.clientData.isAnonymousStatement
                ? InvoiceFilter.Statement
                : InvoiceFilter.PastDue;
    };

    @action reset = () => {
        this.selectedCustomer = undefined;
        this.selectedFilter = DEFAULT_FILTER;
    };

    @action clearSearch = async () => {
        this.searchTerm.reset();
    };

    private initCustomerMap = (customers?: CustomerModel[]) => {
        this.customerMap.clear();
        customers?.forEach((customer) => {
            const extendedCustomer = customer as ExtendedCustomerModel;
            extendedCustomer.balance = customer.invoices.reduce((sum, inv) => sum + inv.balance, 0);
            this.customerMap.set(customer.id, extendedCustomer);
        });
    };

    private initCustomerDropdownState = (customers?: CustomerModel[]) => {
        if (!customers) return;
        const options = customers.map(
            (customer: CustomerModel) =>
            ({
                value: customer.id,
                text: `${customer.name} ${customer.address.streetAddress} ${customer.address.city}, ${customer.address.state} ${customer.address.city}`,
            } as CustomerAnvilSelectOptionsProps)
        );
        const dropdownDataSource = new InMemoryDataSource<string, CustomerAnvilSelectOptionsProps>(
            options
        );
        this.customerDropdownState = new DropdownState(dropdownDataSource);
    };

    private setDataSource = async () => {
        this.gridState.reset();
        await this.gridState.setDataSource(
            new InMemoryGridDataSource<InvoiceRowModel, string>(
                this.filteredInvoices,
                (row) => row.invoiceId
            )
        );
    };

    private mapInvoiceColumns = (invoices: Invoice[]): InvoiceRowModel[] => {
        if (!invoices) return [];
        return invoices.map((invoice) => {
            return <InvoiceRowModel>{
                invoiceId: invoice.id,
                invoiceNumber: invoice.number,
                serviceDate: invoice.serviceDate ? Date.parse(invoice.serviceDate) : undefined,
                serviceAddress: address(invoice.address),
                dueDate: invoice.dueDate ? Date.parse(invoice.dueDate) : undefined,
                invoiceDate: invoice.billingDate ? Date.parse(invoice.billingDate) : undefined,
                invoiceTotal: invoice.total,
                paymentTotal: invoice.paymentTotal,
                invoiceBalance: invoice.balance,
                usesPaymentsService: invoice.usesPaymentsService,
            };
        });
    };

    @action changeFilter = async (filter: InvoiceFilter) => {
        this.selectedFilter = filter;
        await this.setDataSource();
    };

    @action makePayment = () => {
        this.chargeInfoService.paymentAmount = parseFloat(this.paymentAmountTotal.toFixed(2));
        this.chargeInfoService.statementBalance = this.statementBalance;
        this.chargeInfoService.outstandingBalance = this.outstandingBalance;
        this.chargeInfoService.splits = Array.from(this.selectedInvoices).map((invoice) => {
            return { invoiceId: invoice.invoiceId, amountApplied: invoice.invoiceBalance, usesPaymentsService: invoice.usesPaymentsService };
        });
    };

    @computed get isMultipleCustomers(): boolean {
        return this.customerMap.size > 1;
    }

    @computed get isPaymentAllowed(): boolean {
        if (!this.selectedCustomer) return false;
        return !this.selectedCustomer!.creditCardDisabled || !this.selectedCustomer!.achDisabled || !this.selectedCustomer!.payByBankDisabled;
    }

    @computed get overdueInvoices(): Invoice[] {
        if (!this.selectedCustomer) return [];
        const overdue =
            this.selectedCustomer!.invoices.filter(
                (inv) => !!inv.dueDate && moment().isAfter(inv.dueDate, 'd')
            ) || [];
        return overdue;
    }

    @computed get statementInvoices(): Invoice[] {
        if (!this.selectedCustomer) return [];
        const statementInvoices =
            this.selectedCustomer!.invoices.filter(
                (inv) =>
                    !!inv.dueDate &&
                    moment(this.selectedCustomer!.statementSentDate).isSameOrAfter(
                        inv.invoiceDate,
                        'd'
                    )
            ) || [];
        return statementInvoices;
    }

    @computed get filteredInvoices(): InvoiceRowModel[] {
        switch (this.selectedFilter) {
            case InvoiceFilter.PastDue:
                return this.mapInvoiceColumns(this.overdueInvoices);
            case InvoiceFilter.Statement:
                return this.mapInvoiceColumns(this.statementInvoices);
            case InvoiceFilter.All:
                if (!this.selectedCustomer) return [];
                return this.mapInvoiceColumns(this.selectedCustomer!.invoices);
        }
    }

    @computed get shouldShowDueDate(): boolean {
        return this.filteredInvoices.some(x => !!x.dueDate);
    }

    @computed get selectedInvoices(): Set<InvoiceRowModel> {
        if (this.gridState.selectedCount > 0) {
            return new Set<InvoiceRowModel>(
                this.filteredInvoices.filter((inv) => this.gridState.selectedIds.has(inv.invoiceId))
            );
        }
        return new Set<InvoiceRowModel>(this.filteredInvoices);
    }

    @computed get selectedInvoiceCount(): number {
        return this.selectedInvoices.size;
    }

    @computed get filteredInvoiceCount(): number {
        return this.filteredInvoices.length;
    }

    @computed get overdueInvoiceCount(): number {
        return this.overdueInvoices.length;
    }

    @computed get invoiceAmountTotal(): number {
        return this.filteredInvoices.reduce((sum, inv) => sum + inv.invoiceTotal, 0);
    }

    @computed get previousPaymentTotal(): number {
        return this.filteredInvoices.reduce((sum, inv) => sum + inv.paymentTotal, 0);
    }

    @computed get invoiceBalanceTotal(): number {
        return this.filteredInvoices.reduce((sum, inv) => sum + inv.invoiceBalance, 0);
    }

    @computed get paymentAmountTotal(): number {
        return Array.from(this.selectedInvoices).reduce((sum, inv) => sum + inv.invoiceBalance, 0);
    }

    @computed get statementBalance(): number {
        return this.statementInvoices.reduce((sum, inv) => sum + inv.balance, 0);
    }

    @computed get outstandingBalance(): number {
        if (!this.selectedCustomer) return 0;
        return this.selectedCustomer.invoices.reduce((sum, inv) => sum + inv.balance, 0);
    }
}
